/**
 * CalculatorWizard — Progressive disclosure calculator (8 steps).
 *
 * Steps: 0=Medium, 1=Artwork, 2=Surface, 3=Hours, 4=Materials, 5=Detail, 6=SalesChannel, 7=Price
 * Completed steps collapse into tappable summary pills.
 * After step 7: VerdictOverlay → NameSheet → Firestore save.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { T } from '@/styles/tokens';
import { useAuth } from '@/contexts/AuthContext';
import { trackEvent } from '@/services/telemetry';
import { useTrackFlow } from '@/hooks/useTelemetry';
import { useCalculatorForm } from '@/hooks/useCalculatorForm';
import { calculateFloor, calculateVerdict } from '@/lib/engine';
import { MEDIUMS } from '@/types/medium';
import { TIERS } from '@/types/tier';
import { estimateMaterialCents, getFramingEstimateCents } from '@/constants/materials';
import { estimateHours } from '@/constants/phases';
import { CHANNEL_COMMISSION, SALES_CHANNEL_KEYS, computeRetailPrice } from '@/constants/channels';
import { getDefaultSubstrate, getSubstrateOptions, getSubstrateGroup, getSubstrateModifier } from '@/constants/substrates';
import type { SalesChannelKey } from '@/constants/channels';
import type { MediumKey } from '@/types/medium';
import type { TierKey } from '@/types/tier';
import type { Verdict } from '@/types/verdict';

import { WizardHeader } from './WizardHeader';
import { useSubscription } from '@/hooks/useSubscription';
import { PriceCheckCounter } from '@/components/subscription/PriceCheckCounter';
import { FloorBadge } from './FloorBadge';
import { CollapsedPill } from './CollapsedPill';
import { MediumGrid } from './MediumGrid';
import { ArtworkStep } from './ArtworkStep';
import { SurfaceStep } from './SurfaceStep';
import { HoursStep } from './HoursStep';
import { MaterialsStep } from './MaterialsStep';
import { DetailLevelStep } from './DetailLevelStep';
import { SalesChannelStep } from './SalesChannelStep';
import { AskingPriceStep } from './AskingPriceStep';
import { MEDIUM_ICONS } from './MediumIcons';
import { SECTION_ICONS } from './SectionIcons';
import { VerdictOverlay } from '@/components/verdict/VerdictOverlay';
import { NameSheet } from '@/components/verdict/NameSheet';

// ── Types ──────────────────────────────────────────────────────

export type VerdictData = Omit<
  Verdict,
  'id' | 'userId' | 'sold' | 'soldPrice' | 'soldDate' | 'createdAt' | 'updatedAt'
>;

export interface CalculatorWizardProps {
  mode?: 'quote' | 'verdict';
  onVerdictReady?: (verdict: VerdictData, title: string) => void;
  onBack?: () => void;
  existingTitles?: string[];
  recentDimensions?: Array<{ w: string; h: string }>;
}

// ── Constants ──────────────────────────────────────────────────

const STEP_COUNT = 8;

const STEP_LABELS: Record<number, string> = {
  0: 'Medium',
  1: 'Artwork',
  2: 'Surface',
  3: 'Hours',
  4: 'Materials',
  5: 'Detail Level',
  6: 'Sales Channel',
  7: 'Asking Price',
};

// ── Component ──────────────────────────────────────────────────

export function CalculatorWizard({
  onVerdictReady,
  onBack,
  existingTitles = [],
}: CalculatorWizardProps) {
  const { profile } = useAuth();

  // Telemetry
  const flow = useTrackFlow('calculator', 8);
  useEffect(() => {
    trackEvent({ eventType: 'calculator_loaded', screen: 'calculator' });
    return () => { flow.markAbandoned(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reuse existing form hook with profile overrides
  const form = useCalculatorForm(profile ? {
    monthlyBurn: profile.monthlyBurn || undefined,
    monthlyCapacity: profile.monthlyCapacity || undefined,
    careerStage: profile.careerStage || undefined,
    highestSale: profile.highestSale || undefined,
  } : undefined);

  const { formState } = form;
  const { isPremium, priceChecksUsed, priceChecksLimit } = useSubscription();

  // ── Wizard state ──
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [phaseQuantities, setPhaseQuantities] = useState<Map<number, number>>(new Map());
  const [matQuantities, setMatQuantities] = useState<Map<number, number>>(new Map());
  const [showVerdict, setShowVerdict] = useState(false);
  const [showNameSheet, setShowNameSheet] = useState(false);
  const [verdictData, setVerdictData] = useState<VerdictData | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Wizard-local hours/materials tracking (since form hook uses '' | number)
  const [wizardHours, setWizardHours] = useState(0);
  const [hoursIsEstimated, setHoursIsEstimated] = useState(true);
  const [wizardMaterialsCents, setWizardMaterialsCents] = useState(0);
  const [wizardGutPriceCents, setWizardGutPriceCents] = useState(0);
  const [materialIsEstimated, setMaterialIsEstimated] = useState(true);
  const [framingCents, setFramingCents] = useState(0);
  const [framingIsEstimated, setFramingIsEstimated] = useState(true);
  const [framingWaived, setFramingWaived] = useState(false);
  const [salesChannel, setSalesChannel] = useState<SalesChannelKey>('direct');
  const [channelPcts, setChannelPcts] = useState<Record<string, number>>({
    gallery: 50,
    online_platform: 35,
    consignment: 40,
  });
  const [substrate, setSubstrate] = useState(() => getDefaultSubstrate(formState.medium));

  useEffect(() => { setTimeout(() => setLoaded(true), 50); }, []);

  // Auto-estimate hours when dimensions, medium, or tier change (if still in estimated state)
  useEffect(() => {
    if (!hoursIsEstimated) return;
    const width = typeof formState.width === 'number' ? formState.width : 0;
    const height = typeof formState.height === 'number' ? formState.height : 0;
    const area = width * height;
    const estimated = estimateHours(formState.medium, area > 0 ? area : undefined, formState.tier);
    if (estimated > 0) setWizardHours(estimated);
  }, [hoursIsEstimated, formState.medium, formState.width, formState.height, formState.tier]);

  // Auto-estimate materials when dimensions or medium change (if still in estimated state)
  useEffect(() => {
    if (!materialIsEstimated) return;
    const width = typeof formState.width === 'number' ? formState.width : 0;
    const height = typeof formState.height === 'number' ? formState.height : 0;
    const area = width * height;
    if (area <= 0) return;
    setWizardMaterialsCents(estimateMaterialCents(formState.medium, area));
  }, [materialIsEstimated, formState.medium, formState.width, formState.height]);

  // Auto-estimate framing for paper mediums when dimensions change
  const isPaperBased = MEDIUMS[formState.medium]?.isPaperBased ?? false;
  useEffect(() => {
    if (!isPaperBased) {
      setFramingCents(0);
      return;
    }
    if (framingWaived) {
      setFramingCents(0);
      return;
    }
    if (!framingIsEstimated) return;
    const width = typeof formState.width === 'number' ? formState.width : 0;
    const height = typeof formState.height === 'number' ? formState.height : 0;
    const area = width * height;
    if (area <= 0) return;
    setFramingCents(getFramingEstimateCents(area));
  }, [isPaperBased, framingWaived, framingIsEstimated, formState.width, formState.height]);

  // Total materials = material estimate + framing
  const totalMaterialsCents = wizardMaterialsCents + framingCents;

  // ── Floor calculation (real engine) ──
  const floorResult = useMemo(() => {
    if (wizardHours <= 0) return null;
    try {
      return calculateFloor(
        wizardHours,
        formState.monthlyBurn,
        formState.monthlyCapacity,
        totalMaterialsCents,
        formState.tier,
      );
    } catch {
      return null;
    }
  }, [wizardHours, formState.monthlyBurn, formState.monthlyCapacity, totalMaterialsCents, formState.tier]);

  const floorCents = floorResult?.ttcp ?? 0;

  // ── Effective hourly + survival target ──
  const survivalTarget = useMemo(() => {
    if (formState.monthlyCapacity <= 0) return 25; // fallback
    return Math.round(formState.monthlyBurn / formState.monthlyCapacity / 100);
  }, [formState.monthlyBurn, formState.monthlyCapacity]);

  const effectiveHourly = useMemo(() => {
    if (wizardHours <= 0) return 0;
    return Math.round((wizardGutPriceCents - totalMaterialsCents) / wizardHours / 100);
  }, [wizardGutPriceCents, totalMaterialsCents, wizardHours]);

  // ── Step advancement ──
  // When all other steps are already complete, skip to review mode (activeStep = STEP_COUNT)
  // instead of forcing re-traversal through completed steps.
  const advanceStep = useCallback((fromStep: number) => {
    setCompletedSteps((s) => new Set([...s, fromStep]));
    const allOthersComplete = Array.from({ length: STEP_COUNT }, (_, i) => i)
      .filter(i => i !== fromStep)
      .every(i => completedSteps.has(i));
    if (allOthersComplete) {
      setActiveStep(STEP_COUNT); // review mode: all pills + diagnose
    } else if (fromStep < STEP_COUNT - 1) {
      setActiveStep(fromStep + 1);
    }
  }, [completedSteps]);

  const reopenStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  // ── Medium selection (auto-advances after 180ms) ──
  const handleMediumSelect = useCallback((medium: MediumKey) => {
    // Reset substrate: preserve if same group, reset if group changes
    const oldGroup = getSubstrateGroup(formState.medium);
    const newGroup = getSubstrateGroup(medium);
    if (oldGroup !== newGroup) {
      setSubstrate(getDefaultSubstrate(medium));
    }
    form.setMedium(medium);
    // Reset accumulators when medium changes
    setPhaseQuantities(new Map());
    setMatQuantities(new Map());
    setWizardHours(0);
    setHoursIsEstimated(true);
    setWizardMaterialsCents(0);
    setMaterialIsEstimated(true);
    setFramingCents(0);
    setFramingIsEstimated(true);
    setFramingWaived(false);
    setSalesChannel('direct');
    setChannelPcts({ gallery: 50, online_platform: 35, consignment: 40 });
    form.setHours('');
    form.setMaterials(null);
    setTimeout(() => advanceStep(0), 180);
  }, [form, formState.medium, advanceStep]);

  // ── Phase quantity handlers (hours) ──
  const handlePhaseIncrement = useCallback((idx: number, hrs: number) => {
    setHoursIsEstimated(false);
    setWizardHours((h) => h + hrs);
    setPhaseQuantities((prev) => { const n = new Map(prev); n.set(idx, (prev.get(idx) || 0) + 1); return n; });
  }, []);

  const handlePhaseDecrement = useCallback((idx: number, hrs: number) => {
    setHoursIsEstimated(false);
    const current = phaseQuantities.get(idx) || 0;
    if (current <= 1) return;
    setWizardHours((h) => Math.max(0, h - hrs));
    setPhaseQuantities((prev) => { const n = new Map(prev); n.set(idx, current - 1); return n; });
  }, [phaseQuantities]);

  const handlePhaseClear = useCallback((idx: number, totalHrs: number) => {
    setHoursIsEstimated(false);
    setWizardHours((h) => Math.max(0, h - totalHrs));
    setPhaseQuantities((prev) => { const n = new Map(prev); n.delete(idx); return n; });
  }, []);

  // ── Material quantity handlers ──
  const handleMatIncrement = useCallback((idx: number, unitCostDollars: number) => {
    setMaterialIsEstimated(false);
    setWizardMaterialsCents((m) => m + unitCostDollars * 100);
    setMatQuantities((prev) => { const n = new Map(prev); n.set(idx, (prev.get(idx) || 0) + 1); return n; });
  }, []);

  const handleMatDecrement = useCallback((idx: number, unitCostDollars: number) => {
    setMaterialIsEstimated(false);
    const current = matQuantities.get(idx) || 0;
    if (current <= 1) return; // use clearItem to remove entirely
    setWizardMaterialsCents((m) => Math.max(0, m - unitCostDollars * 100));
    setMatQuantities((prev) => { const n = new Map(prev); n.set(idx, current - 1); return n; });
  }, [matQuantities]);

  const handleMatClear = useCallback((idx: number, totalDollars: number) => {
    setMaterialIsEstimated(false);
    setWizardMaterialsCents((m) => Math.max(0, m - totalDollars * 100));
    setMatQuantities((prev) => { const n = new Map(prev); n.delete(idx); return n; });
  }, []);

  // ── Run Diagnosis ──
  const handleRunDiagnosis = useCallback(() => {
    // Sync wizard state to form hook for getVerdictInputs
    form.setHours(wizardHours);
    form.setMaterials(totalMaterialsCents);
    form.setGutPrice(wizardGutPriceCents);

    // Build inputs directly (bypass form validation since wizard validates per-step)
    const w = typeof formState.width === 'number' ? formState.width : 0;
    const h = typeof formState.height === 'number' ? formState.height : 0;

    const inputs = {
      medium: formState.medium,
      width: w,
      height: h,
      hours: wizardHours,
      materials: totalMaterialsCents,
      materialEstimate: wizardMaterialsCents,
      framingEstimate: framingCents,
      framingWaived,
      gutPrice: wizardGutPriceCents,
      tier: formState.tier,
      monthlyBurn: formState.monthlyBurn,
      monthlyCapacity: formState.monthlyCapacity,
      careerStage: formState.careerStage,
      highestSale: formState.highestSale ?? null,
      substrate,
      substrateModifier: getSubstrateModifier(formState.medium, substrate),
      salesChannel,
      currency: 'USD' as const,
    };

    try {
      const verdict = calculateVerdict(inputs);

      // Output-layer retail price computation — outside the engine
      // Use custom commission rate if artist overrode the default
      const defaultRate = CHANNEL_COMMISSION[salesChannel].rate;
      const customPct = channelPcts[salesChannel];
      const commission = customPct != null ? customPct / 100 : defaultRate;

      const computeRetailWithCustomRate = (netCents: number) => {
        if (commission <= 0) return netCents;
        return Math.round(netCents / (1 - commission));
      };

      const retailData = commission > 0 ? {
        salesChannel,
        channelCommission: commission,
        retailFloor: computeRetailWithCustomRate(verdict.calculations.ttcp),
        retailCeiling: computeRetailWithCustomRate(verdict.calculations.ceiling),
      } : {
        salesChannel: 'direct' as const,
      };

      setVerdictData({ ...verdict, ...retailData });
      setCompletedSteps((s) => new Set([...s, 7]));
      setShowVerdict(true);

      // Telemetry: report generated + flow complete
      flow.markComplete();
      trackEvent({
        eventType: 'report_generated', screen: 'calculator',
        meta: {
          medium: formState.medium || '',
          width: formState.width || 0,
          height: formState.height || 0,
          tier: formState.tier || '',
          sqInches: (formState.width || 0) * (formState.height || 0),
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Verdict calculation failed:', err);
    }
  }, [form, formState, wizardHours, totalMaterialsCents, wizardMaterialsCents, framingCents, framingWaived, wizardGutPriceCents, salesChannel, channelPcts]);

  // ── Verdict price stepping ──
  const handleStepPrice = useCallback((delta: number) => {
    setWizardGutPriceCents((p) => Math.max(0, p + delta));
  }, []);

  // ── Save flow ──
  const handleSave = useCallback(() => {
    setShowNameSheet(true);
  }, []);

  // Called by NameSheet.onSave — triggers Firestore write but does NOT close/reset.
  // The form stays visible behind the confirmation overlay so there's no flash.
  const handleNameSave = useCallback((title: string) => {
    if (verdictData && onVerdictReady) {
      onVerdictReady(verdictData, title);
    }
  }, [verdictData, onVerdictReady]);

  // Called by NameSheet.onClose — after confirmation auto-dismisses (~1.5s).
  // Parent (CalculatorPage) handles navigation via handleVerdictReady's own timer.
  // Just close local overlays. Full form reset is moot since parent navigates away.
  const handleNameSheetClose = useCallback(() => {
    setShowNameSheet(false);
    setShowVerdict(false);
  }, []);

  // ── Back button ──
  const handleBack = useCallback(() => {
    if (activeStep === STEP_COUNT) {
      // Review mode: go back to last step
      setActiveStep(STEP_COUNT - 1);
    } else if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else if (onBack) {
      onBack();
    }
  }, [activeStep, onBack]);

  // ── Step summaries for collapsed pills ──
  const mediumLabel = MEDIUMS[formState.medium]?.label || formState.medium;
  const w = typeof formState.width === 'number' ? formState.width : 0;
  const h = typeof formState.height === 'number' ? formState.height : 0;

  // Substrate label for pill summary
  const substrateLabel = getSubstrateOptions(formState.medium).find((o) => o.key === substrate)?.label || substrate;

  // Channel summary for pill
  const channelConfig = CHANNEL_COMMISSION[salesChannel];
  const channelPct = channelPcts[salesChannel] ?? Math.round(channelConfig.rate * 100);
  const channelSummaryText = salesChannel === 'direct'
    ? 'Direct'
    : `${channelConfig.label.replace(/\s*\(\d+%\)/, '')} ${channelPct}%`;

  const stepSummaries: Record<number, string> = {
    0: mediumLabel,
    1: `${w}\u00D7${h}" \u2014 ${formState.depth}" deep`,
    2: substrateLabel,
    3: `${wizardHours} hr${wizardHours !== 1 ? 's' : ''}`,
    4: totalMaterialsCents > 0 ? `$${Math.round(totalMaterialsCents / 100)}` : 'None',
    5: TIERS[formState.tier]?.label || formState.tier,
    6: channelSummaryText,
    7: wizardGutPriceCents > 0 ? `$${Math.round(wizardGutPriceCents / 100).toLocaleString()}` : '\u2014',
  };

  const mediumIconKey = formState.medium === 'mixed_media' ? 'mixed_media' : formState.medium;
  const stepIcons: Record<number, React.ReactNode> = {
    0: MEDIUM_ICONS[mediumIconKey]?.(T.primary),
    1: SECTION_ICONS.artwork,
    2: SECTION_ICONS.surface,
    3: SECTION_ICONS.clock,
    4: SECTION_ICONS.materials,
    5: SECTION_ICONS.detail,
    6: SECTION_ICONS.channel,
    7: SECTION_ICONS.price,
  };

  // ── Render collapsed pills (before, after, or all relative to activeStep) ──
  const renderCollapsedPills = (position: 'before' | 'after' | 'all') => {
    const pills: React.ReactNode[] = [];
    const start = position === 'after' ? activeStep + 1 : 0;
    const end = position === 'before' ? Math.min(activeStep, STEP_COUNT) : STEP_COUNT;
    for (let i = start; i < end; i++) {
      if (completedSteps.has(i) && i !== activeStep) {
        pills.push(
          <CollapsedPill
            key={`pill-${i}`}
            icon={stepIcons[i]}
            label={STEP_LABELS[i]}
            summary={stepSummaries[i]}
            onTap={() => reopenStep(i)}
          />,
        );
      }
    }
    return pills;
  };

  // True when user taps a pill to re-edit a previously completed step
  const isRevisiting = completedSteps.size >= STEP_COUNT && activeStep < STEP_COUNT;

  const stepEnterStyle: React.CSSProperties = {
    opacity: loaded ? 1 : 0,
    transform: loaded ? 'translateY(0)' : 'translateY(12px)',
    transition: `all 0.35s ${T.easeEntrance}`,
  };

  // Dismiss header shown above step content when revisiting — collapses back to review
  const revisitHeader = isRevisiting ? (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 10,
    }}>
      <span style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em',
        color: T.charcoal300,
        fontFamily: T.fontFamily,
      }}>
        Editing {STEP_LABELS[activeStep]}
      </span>
      <button
        onClick={() => setActiveStep(STEP_COUNT)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '5px 12px',
          borderRadius: 999,
          border: `1px solid ${T.border}`,
          background: T.white,
          cursor: 'pointer',
          fontFamily: T.fontFamily,
          fontSize: 12,
          fontWeight: 600,
          color: T.primary,
        }}
      >
        Done
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
          <path d="M3 8.5l3.5 3.5 6.5-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  ) : null;

  return (
    <div style={{
      minHeight: '100dvh',
      background: T.cream,
      fontFamily: T.fontFamily,
      maxWidth: 430,
      margin: '0 auto',
      position: 'relative',
      overflowX: 'hidden',
    }}>
      <WizardHeader
        activeStep={activeStep}
        totalSteps={STEP_COUNT}
        onBack={handleBack}
        canGoBack={activeStep > 0 || activeStep === STEP_COUNT}
      />

      {!isPremium && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 16px 0' }}>
          <PriceCheckCounter used={priceChecksUsed} limit={priceChecksLimit} isPremium={isPremium} />
        </div>
      )}

      <FloorBadge floorCents={floorCents} visible={wizardHours > 0 && (activeStep >= 3 || activeStep === STEP_COUNT)} />

      <main style={{ padding: '0 16px', paddingBottom: 40 }}>
        {activeStep < STEP_COUNT && renderCollapsedPills('before')}

        {/* Review mode: all steps complete, show all pills + diagnose */}
        {activeStep === STEP_COUNT && (
          <div style={stepEnterStyle}>
            {renderCollapsedPills('all')}
            <button
              onClick={handleRunDiagnosis}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: '100%',
                padding: 14,
                marginTop: 16,
                borderRadius: 14,
                border: 'none',
                background: T.primary,
                color: T.white,
                fontFamily: T.fontFamily,
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: `0 4px 16px ${T.tealGlow}`,
              }}
            >
              Diagnose
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}

        {/* Step 0: Medium */}
        {activeStep === 0 && (
          <div style={stepEnterStyle}>
            {revisitHeader}
            <MediumGrid selected={formState.medium} onSelect={handleMediumSelect} />
          </div>
        )}

        {/* Step 1: Artwork (Dimensions + Depth) */}
        {activeStep === 1 && (
          <div style={stepEnterStyle}>
            {revisitHeader}
            <ArtworkStep
              width={w}
              height={h}
              depth={formState.depth}
              onChangeWidth={(v) => form.setWidth(v)}
              onChangeHeight={(v) => form.setHeight(v)}
              onChangeDepth={(d) => form.setDepth(d)}
              onNext={() => advanceStep(1)}
            />
          </div>
        )}

        {/* Step 2: Surface */}
        {activeStep === 2 && (
          <div style={stepEnterStyle}>
            {revisitHeader}
            <SurfaceStep
              medium={formState.medium}
              substrate={substrate}
              onChangeSubstrate={setSubstrate}
              onNext={() => advanceStep(2)}
            />
          </div>
        )}

        {/* Step 3: Hours */}
        {activeStep === 3 && (
          <div style={stepEnterStyle}>
            {revisitHeader}
            <HoursStep
              hours={wizardHours}
              medium={formState.medium}
              phaseQuantities={phaseQuantities}
              onChangeHours={(h) => { setHoursIsEstimated(false); setWizardHours(h); }}
              onIncrementPhase={handlePhaseIncrement}
              onDecrementPhase={handlePhaseDecrement}
              onClearPhase={handlePhaseClear}
              onResetPhases={() => { setPhaseQuantities(new Map()); setWizardHours(0); setHoursIsEstimated(true); }}
              onNext={() => advanceStep(3)}
            />
          </div>
        )}

        {/* Step 4: Materials */}
        {activeStep === 4 && (
          <div style={stepEnterStyle}>
            {revisitHeader}
            <MaterialsStep
              materialsCents={wizardMaterialsCents}
              medium={formState.medium}
              area={w * h}
              matQuantities={matQuantities}
              onChangeMaterials={(cents) => { setMaterialIsEstimated(false); setWizardMaterialsCents(cents); }}
              onIncrementMat={handleMatIncrement}
              onDecrementMat={handleMatDecrement}
              onClearMat={handleMatClear}
              onResetMats={() => { setMatQuantities(new Map()); setWizardMaterialsCents(0); setMaterialIsEstimated(true); }}
              onNext={() => advanceStep(4)}
              framingCents={framingCents}
              framingWaived={framingWaived}
              onToggleFramingWaived={(waived) => { setFramingWaived(waived); if (waived) setFramingCents(0); else setFramingIsEstimated(true); }}
            />
          </div>
        )}

        {/* Step 5: Detail Level */}
        {activeStep === 5 && (
          <div style={stepEnterStyle}>
            {revisitHeader}
            <DetailLevelStep
              tier={formState.tier}
              onChangeTier={(t) => form.setTier(t)}
              onNext={() => advanceStep(5)}
            />
          </div>
        )}

        {/* Step 6: Sales Channel */}
        {activeStep === 6 && (
          <div style={stepEnterStyle}>
            {revisitHeader}
            <SalesChannelStep
              channel={salesChannel}
              channelPcts={channelPcts}
              onChangeChannel={setSalesChannel}
              onChangeChannelPct={(key, pct) => setChannelPcts((prev) => ({ ...prev, [key]: pct }))}
              onNext={() => advanceStep(6)}
            />
          </div>
        )}

        {/* Step 7: Asking Price */}
        {activeStep === 7 && (
          <div style={stepEnterStyle}>
            {revisitHeader}
            <AskingPriceStep
              gutPriceCents={wizardGutPriceCents}
              onChangeGutPrice={setWizardGutPriceCents}
              onRunDiagnosis={handleRunDiagnosis}
            />
          </div>
        )}

        {/* Pills below active step (completed steps after current) */}
        {activeStep < STEP_COUNT && Array.from(completedSteps).some(i => i > activeStep) && (
          <div style={{
            height: 1,
            background: T.border,
            margin: '12px 0 8px',
            opacity: 0.6,
          }} />
        )}
        {activeStep < STEP_COUNT && renderCollapsedPills('after')}
      </main>

      {/* Verdict Overlay */}
      <VerdictOverlay
        visible={showVerdict}
        floorCents={floorCents}
        priceCents={wizardGutPriceCents}
        hours={wizardHours}
        materialsCents={totalMaterialsCents}
        medium={mediumLabel}
        mediumKey={formState.medium}
        width={w}
        height={h}
        tier={TIERS[formState.tier]?.label || formState.tier}
        effectiveHourly={effectiveHourly}
        survivalTarget={survivalTarget}
        monthlyBurn={formState.monthlyBurn}
        monthlyCapacity={formState.monthlyCapacity}
        isPremium={false}
        onAdjust={() => { setShowVerdict(false); setActiveStep(STEP_COUNT); }}
        onPriceChange={(cents) => setWizardGutPriceCents(cents)}
        onSave={handleSave}
        onClose={() => { setShowVerdict(false); setActiveStep(STEP_COUNT); }}
        salesChannel={salesChannel}
        channelLabel={CHANNEL_COMMISSION[salesChannel].label}
        retailFloorCents={verdictData?.retailFloor}
        retailCeilingCents={verdictData?.retailCeiling}
      />

      {/* Name Sheet */}
      <NameSheet
        isOpen={showNameSheet}
        onClose={handleNameSheetClose}
        onSave={handleNameSave}
        existingTitles={existingTitles}
        medium={mediumLabel}
        width={w}
        height={h}
        askingPrice={wizardGutPriceCents}
      />

      <style>{`
        * { box-sizing: border-box; }
        input::placeholder { color: ${T.charcoal300}; font-weight: 400; }
      `}</style>
    </div>
  );
}
